import { HttpClient, HttpHeaders } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { BehaviorSubject, finalize, Observable, take } from 'rxjs'
import { KeyingAppEnvironmentConfig } from '../configuration/keying.app.environment.config'
import { KeySystem, Note } from '../models/dev.test.models'
import { sortBy, groupBy } from 'underscore'
import { LoadingService } from './loading.service'
import { MasterKey } from '@overtur-monorepo/keying-hierarchy'
import { ApiService } from '@overtur-monorepo/keying-shared'
import { HierarchyConfig } from 'libs/keying-hierarchy/src/lib/configuration/keying.hierarchy.environment.config'
import { LoggingService } from './logging.service'

export enum Accept {
  AddSplKey = 'application/allegion.keying.add.operator+json',
  RemoveSplKey = 'application/allegion.keying.remove.operator+json',
}

@Injectable({
  providedIn: 'root',
})
export class KeyingSystemService {
  keySystemId = new BehaviorSubject<number>(null)

  keySystems = new BehaviorSubject<KeySystem[]>([])
  keySystems$ = this.keySystems.asObservable()

  keySystem = new BehaviorSubject<KeySystem>(null)
  keySystem$ = this.keySystem.asObservable()

  deletedMasterKey = new BehaviorSubject<MasterKey>(null)
  deletedMasterKey$ = this.deletedMasterKey.asObservable()

  constructor(
    @Inject(HttpClient) private httpClient: HttpClient,
    private keyingAppEnvironmentConfig: KeyingAppEnvironmentConfig,
    private loadingService: LoadingService,
    private api: ApiService,
    private config: HierarchyConfig,
    private loggingService: LoggingService
  ) {}

  setKeySystems(keySystems: KeySystem[]) {
    this.keySystems.next(keySystems)
  }

  selectKeySystem(keySystem: KeySystem) {
    this.keySystem.next(keySystem)
  }

  deleteMasterKey(masterKey: MasterKey) {
    this.deletedMasterKey.next(masterKey)
  }

  setKeySystemById(keySystemId) {
    this.loggingService.log('Set Key System by ID:', keySystemId)
    this.keySystems$.pipe(take(1)).subscribe((keySystems: KeySystem[]) => {
      keySystems.forEach((keySystem) => {
        if (keySystem.id == keySystemId) {
          this.loggingService.log('keySystem Found!', keySystem)
          this.selectKeySystem(keySystem)
        }
      })
    })
  }

  removeKeySystem(KeySystemId, structureId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${KeySystemId}`
    return this.httpClient.delete<any[]>(url)
  }

  createNewKeySystem(keySystem, structureId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/KeySystems`
    return this.httpClient.post<any>(url, keySystem)
  }

  getKeySystem(structureId, keySystemId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/KeySystems/${keySystemId}`
    return this.httpClient.get<any>(url)
  }

  getKeySystems(structureId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/KeySystems`
    return this.httpClient.get<any>(url)
  }

  patchRckTmk(structureId, KeySystemId, options, isRCK = false) {
    this.loadingService.set({ isLoading: true, message: 'Saving TMK' })
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/KeySystems/${KeySystemId}`

    let type

    if (isRCK) {
      type = 'application/allegion.keying.rck.updatetopmasterkey+json'
    } else {
      type = 'application/allegion.keying.updatetopmasterkey+json'
    }

    let headers = new HttpHeaders().set('Content-Type', type)
    return this.httpClient.patch<any>(url, options, { headers })
  }

  generateTmk(structureId, KeySystemId, options) {
    this.loadingService.set({ isLoading: true, message: 'Generating TMK' })
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/KeySystems/${KeySystemId}`
    let type = 'application/allegion.keying.generatetopmasterkey+json'

    const headers = new HttpHeaders().set('Content-Type', type)
    return this.httpClient.patch<any>(url, options, { headers })
  }

  addOrRemoveSpecialFunctionKey(
    { keySetSymbol, competitorId, cylinderIds },
    { structureId, keySystemId },
    contentType: Accept,
    auditTrailRequest: any = undefined
  ) {
    let message
    if (contentType === Accept.AddSplKey) {
      message = 'Add Special Function Key'
    } else {
      message = 'Remove Special Function Key'
    }
    this.loadingService.set({ isLoading: true, message: message })
    return this.httpClient
      .put(
        `${this.keyingAppEnvironmentConfig.baseCylinderApiUrl()}/structures/${structureId}/KeySystems/${keySystemId}/Cylinders`,
        { keySetSymbol, competitorId, cylinderIds, auditTrailRequest: auditTrailRequest || {} },
        {
          headers: {
            'Content-Type': contentType,
          },
        }
      )
      .pipe(
        finalize(() => {
          this.loadingService.set({ isLoading: false, message: '' })
        })
      )
  }

  // Previous method
  // addConstructionKey(structureId, KeySystemId, masterKeyId, options) {
  //   this.loadingService.set({ isLoading: true, message: 'Saving Key System' })
  //   const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/KeySystems/${KeySystemId}/masterKeys/${masterKeyId}`

  //   let type = 'application/allegion.keying.addconstructionkey+json'

  //   let headers = new HttpHeaders().set('Content-Type', type)
  //   return this.httpClient.put<any>(url, options, { headers })
  // }

  addConstructionKey(structureId, KeySystemId, options) {
    this.loadingService.set({ isLoading: true, message: 'Saving Key System' })
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/Structures/${structureId}/keySystems/${KeySystemId}`

    let type = 'application/allegion.keying.update.keysystem.constructionkey+json'

    let headers = new HttpHeaders().set('Content-Type', type)
    return this.httpClient.patch<any>(url, options, { headers })
  }

  patchOriginalOrder(structureId, KeySystemId, options) {
    this.loadingService.set({ isLoading: true, message: 'Saving Key System' })
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/Structures/${structureId}/KeySystems/${KeySystemId}`

    let type = 'application/allegion.keying.update.keysystem.order+json'

    let headers = new HttpHeaders().set('Content-Type', type)
    return this.httpClient.patch<any>(url, options, { headers })
  }

  createNewNote(note, structureId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/Notes`
    return this.httpClient.post<any>(url, note)
  }

  editNote(request, structureId, noteId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/Notes/${noteId}`
    return this.httpClient.patch<any>(url, request)
  }

  softDeleteNote(request, structureId, noteId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/Notes/${noteId}`
    return this.httpClient.patch<any>(url, request)
  }

  getKeySystemNotes(structureId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/Notes`
    return this.httpClient.get<any>(url)
  }

  groupNotesByDate(notes) {
    notes = notes.filter((note) => !note.isDeleted)

    if (!notes) {
      return []
    }

    notes.forEach((note) => {
      note.createdOn = new Date(note.createdOn)
    })

    const groupings = groupBy(notes, (note: Note) => {
      return `${note.createdOn.getFullYear()}-${note.createdOn.getMonth() + 1}-${note.createdOn.getDate()}`
    })

    let noteGroupings = []

    for (let grouping in groupings) {
      const a = sortBy(groupings[grouping], 'orderedDate').reverse()
      noteGroupings.push({ date: new Date(grouping), notes: a })
    }

    noteGroupings = sortBy(noteGroupings, 'date').reverse()

    return noteGroupings
  }

  getMasterKeyById(masterKeys: MasterKey, id) {
    const flat = masterKeys.squashIncludeChangeKeys()
    return flat.find((k) => k.id === id)
  }

  getMasterKey(structureId, keySystemId, masterKeyId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}/MasterKeys/${masterKeyId}`

    return this.httpClient.get<any>(url)
  }

  queueForValidation(keySystemId: number, structureId: number) {
    const serviceEndpoint = `structures/${structureId}/keySystems/QueueValidation`
    const request = [
      {
        op: 'replace',
        path: '/StatusId',
        value: 5,
      },
    ]

    const httpHeaders = new HttpHeaders().set(
      'Content-Type',
      'application/allegion.keying.update.keysystem.status+json'
    )

    return this.api.patch<any, any>(this.buildUrl(serviceEndpoint), request, httpHeaders)
  }

  setStatus(structureId: number, keySystemId: number, request) {
    this.loggingService.log('request', request)
    const serviceEndpoint = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/Structures/${structureId}/keySystems/${keySystemId}`
    var httpHeaders = new HttpHeaders().set('Content-Type', 'application/allegion.keying.update.keysystem.status+json')

    return this.api.patch<any, any>(serviceEndpoint, request, httpHeaders)
  }

  patchKeySystem(structureId: number, keySystemId: number, request) {
    const serviceEndpoint = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/Structures/${structureId}/keySystems/${keySystemId}`
    const httpHeaders = new HttpHeaders().set('Content-Type', 'application/allegion.keying.update.keysystem+json')

    return this.api.patch<any, any>(serviceEndpoint, request, httpHeaders)
  }

  getMECSReport(structureId: number, keySystemId: number): Observable<Blob> {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}`

    let headers = new HttpHeaders().set('Accept', 'application/allegion.keying.validation.report+json')

    return this.httpClient.get(url, { headers, responseType: 'blob' })
  }

  getPinningReport(structureId, KeySystemId): Observable<Blob> {
    this.loadingService.set({ isLoading: true, message: 'Getting Pinning Report' })
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/KeySystems/${KeySystemId}`

    let headers = new HttpHeaders().set('Accept', 'application/allegion.keying.pinning.report+json')

    return this.httpClient.get(url, { headers, responseType: 'blob' })
  }

  generateShearlinesAndPinning(structureId, KeySystemId) {
    const serviceEndpoint = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/KeySystems/${KeySystemId}`
    var httpHeaders = new HttpHeaders().set(
      'Content-Type',
      'application/allegion.keying.update.shearlines.pinning+json'
    )
    return this.api.post<any, any>(serviceEndpoint, null, httpHeaders)
  }

  queueOfflinePinning(structureId) {
    const serviceEndpoint = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/KeySystems/queueofflinepinning`
    return this.api.post<any, any>(serviceEndpoint, null, null)
  }

  publishStructure(structureId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}`

    const headers = new HttpHeaders().set('Content-Type', 'application/allegion.keying.publish.structure+json')

    return this.httpClient.put<any>(url, {}, { headers })
  }

  overrideMECSErrors(structureId: number, keySystemId: number) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}`

    const headers = new HttpHeaders().set('Content-Type', 'application/allegion.keying.override.mecs.errors+json')

    return this.httpClient.put<any>(url, {}, { headers })
  }

  overrideMECSErrorsAndPublish(structureId) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}`

    const headers = new HttpHeaders().set(
      'Content-Type',
      'application/allegion.keying.override.mecs.errors.and.publish+json'
    )

    return this.httpClient.put<any>(url, {}, { headers })
  }

  private buildUrl(serviceEndpoint: string): string {
    return `${this.config.baseUrl}/${serviceEndpoint}`
  }

  /* istanbul ignore next */
  getMECSStatus(structureId: number) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/mecs/status`

    const headers = new HttpHeaders().set('Content-Type', 'application/json')

    return this.httpClient.get<any>(url, { headers })
  }

  /* istanbul ignore next */
  convertSystemToDealerSpec(structureId: number, keySystemId: number) {
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}`

    const headers = new HttpHeaders().set(
      'Content-Type',
      'application/allegion.keying.convert.to.dealer.spec.system+json'
    )

    return this.httpClient.put<any>(url, {}, { headers })
  }

  /* istanbul ignore next */
  deleteKeySystem(structureId: number, keySystemId: number, auditRequestFormData: any) {
    const type = 'application/json'

    const headers = new HttpHeaders().set('Content-Type', type)
    const url = `${this.keyingAppEnvironmentConfig.getBaseUrl()}/structures/${structureId}/keySystems/${keySystemId}`

    return this.httpClient.delete<any>(url, { headers: headers, body: { auditTrailRequest: auditRequestFormData } })
  }
}
